import { useEffect, useRef, useCallback } from 'react';
import { useBlocker } from 'react-router-dom';

export interface UnsavedChangesOptions {
  hasUnsavedChanges: boolean;
  message?: string;
  onSave?: () => Promise<void> | void;
  onDiscard?: () => void;
  enablePageUnload?: boolean; // Warn when user tries to close/refresh page
  enableNavigation?: boolean; // Block navigation when there are unsaved changes
}

export interface UnsavedChangesState {
  showConfirmDialog: boolean;
  isBlocked: boolean;
  pendingNavigation: (() => void) | null;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  saveAndContinue: () => Promise<void>;
}

export function useUnsavedChanges(options: UnsavedChangesOptions): UnsavedChangesState {
  const {
    hasUnsavedChanges,
    message = 'You have unsaved changes. Are you sure you want to leave without saving?',
    onSave,
    onDiscard,
    enablePageUnload = true,
    enableNavigation = true,
  } = options;

  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const isBlockedRef = useRef(false);
  const showConfirmDialogRef = useRef(false);

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      if (!enableNavigation || !hasUnsavedChanges) {
        return false;
      }
      
      return currentLocation.pathname !== nextLocation.pathname;
    }
  );

  // Handle page unload/refresh
  useEffect(() => {
    if (!enablePageUnload) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, message, enablePageUnload]);

  // Handle blocked navigation
  useEffect(() => {
    if (blocker.state === 'blocked') {
      isBlockedRef.current = true;
      pendingNavigationRef.current = () => blocker.proceed();
      showConfirmDialogRef.current = true;
    } else {
      isBlockedRef.current = false;
      pendingNavigationRef.current = null;
      showConfirmDialogRef.current = false;
    }
  }, [blocker]);

  const confirmNavigation = useCallback(() => {
    onDiscard?.();
    showConfirmDialogRef.current = false;
    
    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    }
    
    isBlockedRef.current = false;
  }, [onDiscard]);

  const cancelNavigation = useCallback(() => {
    if (blocker && 'reset' in blocker && typeof blocker.reset === 'function') {
      blocker.reset();
    }
    showConfirmDialogRef.current = false;
    pendingNavigationRef.current = null;
    isBlockedRef.current = false;
  }, [blocker]);

  const saveAndContinue = useCallback(async () => {
    if (onSave) {
      try {
        await onSave();
        
        // After successful save, proceed with navigation
        showConfirmDialogRef.current = false;
        if (pendingNavigationRef.current) {
          pendingNavigationRef.current();
          pendingNavigationRef.current = null;
        }
        isBlockedRef.current = false;
      } catch (error) {
        // If save fails, don't proceed with navigation
        console.error('Failed to save changes:', error);
        throw error;
      }
    } else {
      confirmNavigation();
    }
  }, [onSave, confirmNavigation]);

  return {
    showConfirmDialog: showConfirmDialogRef.current,
    isBlocked: isBlockedRef.current,
    pendingNavigation: pendingNavigationRef.current,
    confirmNavigation,
    cancelNavigation,
    saveAndContinue,
  };
}

// Simpler hook for form-specific unsaved changes
export function useFormUnsavedChanges<T extends Record<string, any>>(
  initialValues: T,
  currentValues: T,
  options?: Omit<UnsavedChangesOptions, 'hasUnsavedChanges'>
) {
  const hasUnsavedChanges = JSON.stringify(initialValues) !== JSON.stringify(currentValues);
  
  return useUnsavedChanges({
    ...options,
    hasUnsavedChanges,
  });
}